/** One unmatched demand row: the need, its context, an Archived badge once handled, and Archive or Restore. */

import { Archive, ArchiveRestore } from 'lucide-react';

import { useRestoreDemand } from '@/api/queries';
import type { UnmatchedDemandItem } from '@/api/types';
import { Badge } from '@/ui/Badge';
import { formatDate } from '@/ui/format';

import { archivedHint, archivedLabel, SOURCE_LABELS } from './demandUi';

function RowAction({ item, onArchive }: { item: UnmatchedDemandItem; onArchive: (item: UnmatchedDemandItem) => void }) {
  const restore = useRestoreDemand();
  const name = item.summary || 'this demand';
  if (item.archived_at) {
    return (
      <button type="button" className="btn btn-secondary btn-icon" data-tooltip="Restore to the Open view"
        aria-label={`Restore ${name}`} disabled={restore.isPending} aria-busy={restore.isPending || undefined}
        onClick={() => restore.mutate(item.id)}>
        <ArchiveRestore aria-hidden="true" />
      </button>
    );
  }
  return (
    <button type="button" className="btn btn-secondary btn-icon" data-tooltip="Archive with a reason"
      aria-label={`Archive ${name}`} onClick={() => onArchive(item)}>
      <Archive aria-hidden="true" />
    </button>
  );
}

export function DemandRow({ item, onArchive }: { item: UnmatchedDemandItem; onArchive: (item: UnmatchedDemandItem) => void }) {
  return (
    <tr>
      <th scope="row">
        {item.summary || 'No summary'}
        {item.needs.length > 0 && <div className="sub">{item.needs.join('; ')}</div>}
        {item.archived_at && (
          <div className="sub">
            <Badge tone="neutral" title={archivedHint(item)}>{archivedLabel(item)}</Badge>{' '}
            <span className="hint">{archivedHint(item)}</span>
          </div>
        )}
      </th>
      <td>{item.constraints.length ? item.constraints.join('; ') : <span className="hint">None</span>}</td>
      <td>{item.industry ?? <span className="hint">Not stated</span>}</td>
      <td>{item.country ?? <span className="hint">Not stated</span>}</td>
      <td>{SOURCE_LABELS[item.source] ?? item.source}</td>
      <td className="hint">{formatDate(item.created_at)}</td>
      <td><div className="row-actions"><RowAction item={item} onArchive={onArchive} /></div></td>
    </tr>
  );
}
